import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isCoarsePointer, SEARCH_FIELD_PROPS } from '@/components/shared/touchInput';
import { Input } from '@/components/ui/input';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useStudySuggest } from '@/hooks/use-study-suggest';
import { formatDate as formatLocaleDate, NUMERIC_DATE } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CalendarDays, FileText, Hash, IdCard, Loader2, Search, User } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
const MATCHED_FIELD_META = {
    patientName: { icon: User, labelKey: 'studySuggest.field.patientName' },
    patientId: { icon: IdCard, labelKey: 'studySuggest.field.mrn' },
    accessionNumber: { icon: Hash, labelKey: 'studySuggest.field.accession' },
    description: { icon: FileText, labelKey: 'studySuggest.field.description' },
};
export function StudySearchAutosuggest({ value, onChange, onCrossFieldSearch, placeholder, className, }) {
    const { t } = useTranslation();
    const [, setLocation] = useLocation();
    const listboxId = useId();
    const [open, setOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef(null);
    const activeOptionRef = useRef(null);
    const { suggestions, isFetching, debouncedQuery, enabled } = useStudySuggest(value);
    const showDropdown = open && enabled;
    const showLoading = isFetching && suggestions.length === 0;
    const showEmpty = !isFetching && enabled && suggestions.length === 0;
    // Clamp the active descendant during render so a shrinking result set (new
    // query, fewer matches) never leaves arrow-key focus pointing past the end.
    // Typing resets it to -1 in onChange, so we don't need a state-syncing effect.
    const activeIndex = highlightedIndex < suggestions.length ? highlightedIndex : -1;
    // Close on outside click.
    useEffect(() => {
        if (!showDropdown)
            return;
        const onPointerDown = (e) => {
            if (!containerRef.current?.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [showDropdown]);
    // Keep the highlighted row scrolled into view during keyboard nav.
    useEffect(() => {
        activeOptionRef.current?.scrollIntoView({ block: 'nearest' });
    }, [highlightedIndex]);
    // From sm the dropdown is left-anchored to the input, so a fixed 28rem width
    // can run past the right viewport edge when the input sits off-center: its
    // max width is clamped to the space between the input's left edge and the
    // viewport edge (the CSS max-w below stays as a pre-measure fallback). On a
    // phone that space is ~300px, so there the list is pinned to the viewport
    // (8px gutters) just under the input and gets the whole width.
    //
    // The viewport width is documentElement.clientWidth, not innerWidth: on a
    // phone innerWidth grows with any horizontal overflow, including this
    // dropdown's own, so a clamp based on it fed the overflow it was meant to
    // prevent (and iOS also shrinks innerWidth while the page is zoomed).
    const isSmUp = useMediaQuery('(min-width: 640px)', true);
    const [dropdownBox, setDropdownBox] = useState(null);
    useLayoutEffect(() => {
        if (!showDropdown)
            return;
        const update = () => {
            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect)
                return;
            const viewportWidth = document.documentElement.clientWidth;
            setDropdownBox({
                maxWidth: Math.max(160, viewportWidth - rect.left - 8),
                top: rect.bottom + 6,
            });
        };
        update();
        window.addEventListener('resize', update);
        // Capture: any scroller moving the input (the page on a short viewport).
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [showDropdown]);
    const selectSuggestion = (item) => {
        setOpen(false);
        setHighlightedIndex(-1);
        setLocation(`/viewer/${encodeURIComponent(item.id)}`);
    };
    const applyCrossFieldSearch = () => {
        setOpen(false);
        setHighlightedIndex(-1);
        onCrossFieldSearch(value);
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            if (open) {
                e.preventDefault();
                setOpen(false);
                setHighlightedIndex(-1);
            }
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            // A highlighted suggestion opens that study; otherwise broaden to a
            // cross-field worklist filter across all four searchable fields.
            if (showDropdown && activeIndex >= 0 && activeIndex < suggestions.length) {
                selectSuggestion(suggestions[activeIndex]);
            }
            else {
                applyCrossFieldSearch();
            }
            // An on-screen keyboard only closes on blur; left up, it covers half a
            // phone's worth of the results the search just produced.
            if (isCoarsePointer())
                e.currentTarget.blur();
            return;
        }
        if (!showDropdown || suggestions.length === 0) {
            if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && enabled) {
                setOpen(true);
            }
            return;
        }
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
                break;
        }
    };
    const formatDate = (iso) => (iso ? formatLocaleDate(iso, NUMERIC_DATE) : '') || t('studySuggest.unknownDate');
    return (_jsxs("div", { ref: containerRef, className: cn('relative', className), children: [_jsx(Search, { className: "text-muted-foreground/50 pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" }), _jsx(Input, { type: "search", ...SEARCH_FIELD_PROPS, role: "combobox", "aria-expanded": showDropdown, "aria-controls": listboxId, "aria-autocomplete": "list", "aria-activedescendant": activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined, placeholder: placeholder, value: value, onChange: (e) => {
                    onChange(e.target.value);
                    setHighlightedIndex(-1);
                    setOpen(true);
                }, onFocus: () => setOpen(true), onKeyDown: handleKeyDown, className: "text-foreground placeholder:text-muted-foreground/40 h-8 rounded-full border-[#1E293B] bg-[#161B22] pr-8 pl-9 text-xs focus-visible:ring-teal-600/40 pointer-coarse:h-10", "data-testid": "study-search-autosuggest-input" }), isFetching && (_jsx(Loader2, { className: "text-muted-foreground/60 absolute top-1/2 right-3 h-3.5 w-3.5 -translate-y-1/2 animate-spin" })), showDropdown && (_jsxs("div", { className: cn('z-50 overflow-hidden rounded-lg border border-[#1E293B] bg-[#0D1117] shadow-xl shadow-black/40', isSmUp
                    ? 'absolute left-0 mt-1.5 w-[28rem] max-w-[min(28rem,calc(100vw-1rem))]'
                    : 'fixed inset-x-2'), style: dropdownBox === null
                    ? undefined
                    : isSmUp
                        ? { maxWidth: Math.min(dropdownBox.maxWidth, 448) }
                        : { top: dropdownBox.top }, "data-testid": "study-suggest-dropdown", children: [showLoading && (_jsxs("div", { className: "text-muted-foreground flex items-center gap-2 px-3 py-3 text-xs", children: [_jsx(Loader2, { className: "h-3.5 w-3.5 animate-spin" }), t('studySuggest.loading')] })), showEmpty && (_jsx("div", { className: "text-muted-foreground px-3 py-3 text-xs", children: t('studySuggest.noResults', { query: debouncedQuery }) })), suggestions.length > 0 && (_jsx("ul", { id: listboxId, role: "listbox", "aria-label": t('studySuggest.listLabel'), className: "max-h-80 overflow-y-auto py-1", children: suggestions.map((item, index) => {
                            const meta = MATCHED_FIELD_META[item.matchedField];
                            const FieldIcon = meta.icon;
                            const isActive = index === activeIndex;
                            return (_jsxs("li", { ref: isActive ? activeOptionRef : undefined, id: `${listboxId}-option-${index}`, role: "option", "aria-selected": isActive, onMouseEnter: () => setHighlightedIndex(index), onMouseDown: (e) => {
                                    // Prevent the input blur from closing the dropdown before the click lands.
                                    e.preventDefault();
                                }, onClick: () => selectSuggestion(item), className: cn('flex cursor-pointer items-start gap-2.5 px-3 py-2 transition-colors', isActive ? 'bg-teal-600/15' : 'hover:bg-white/[0.04]'), "data-testid": "study-suggest-option", children: [_jsx(User, { className: "text-muted-foreground/70 mt-0.5 h-3.5 w-3.5 shrink-0" }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsxs("div", { className: "flex items-center justify-between gap-2", children: [_jsx("span", { className: "text-foreground truncate text-xs font-medium", children: item.patientName || t('studySuggest.unknownPatient') }), _jsxs("span", { className: "text-muted-foreground/70 inline-flex shrink-0 items-center gap-1 rounded-full bg-white/[0.06] px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase", children: [_jsx(FieldIcon, { className: "h-3 w-3" }), t(meta.labelKey)] })] }), _jsxs("div", { className: "text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]", children: [item.patientId && (_jsxs("span", { className: "inline-flex items-center gap-1", children: [_jsx(IdCard, { className: "h-3 w-3 opacity-70" }), item.patientId] })), item.accessionNumber && (_jsxs("span", { className: "inline-flex items-center gap-1", children: [_jsx(Hash, { className: "h-3 w-3 opacity-70" }), item.accessionNumber] })), item.modality && (_jsx("span", { className: "rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-[10px]", children: item.modality })), _jsxs("span", { className: "inline-flex items-center gap-1", children: [_jsx(CalendarDays, { className: "h-3 w-3 opacity-70" }), formatDate(item.dateTime)] })] }), item.description && (_jsxs("div", { className: "text-muted-foreground/80 mt-0.5 flex items-center gap-1 text-[11px]", children: [_jsx(FileText, { className: "h-3 w-3 shrink-0 opacity-70" }), _jsx("span", { className: "truncate", children: item.description })] }))] })] }, item.id));
                        }) }))] }))] }));
}
